// @flow
import * as React from 'react';
import ReactSelect from 'react-select';

type RenderableValue = number | string;
type Reference = { [key: string]: RenderableValue };

type OptionParams = {|
  +disabled?: boolean,
  +isGroup?: boolean,
  +type?: string
|};

export type Option = {|
  +label: RenderableValue,
  +value: RenderableValue,
  ...OptionParams
|};

export type Options = $ReadOnlyArray<Option>;

export function getOption(
  label: RenderableValue,
  value: RenderableValue,
  options: Object = {}
): Option {
  return { label, value, ...options };
}

type Values = $ReadOnlyArray<RenderableValue>;

export function arrayToOptions(values: Values): Options {
  return values.map(value => getOption(value, value));
}

export function getOptions(
  values: Values,
  ref: Object,
  label: RenderableValue,
  val: RenderableValue,
  getLabel: Function
): Options {
  return values.reduce((acc, value) => {
    const keys = Object.keys(ref);
    const fieldKey = keys[0].includes('-')
      ? keys.find(k => k.split('-')[1] === value.toString())
      : value;
    if (fieldKey) {
      const reference = ref[fieldKey];
      if (reference) {
        return [
          ...acc,
          getOption(
            getLabel ? getLabel(reference, label) : reference[label],
            fieldKey !== value ? fieldKey : reference[val]
          )
        ];
      }
    }
    return acc;
  }, []);
}

type Props = {
  id: string,
  options: Option[],
  name: string,
  label?: React.Node,
  width?: string,
  value: ?string,
  placeholder?: string,
  className?: string,
  required?: boolean,
  searchable?: boolean,
  type?: string,
  pretty?: boolean,
  onClear?: () => {},
};

export default class Select extends React.Component<Props, void> {
  static defaultProps = {
    type: 'text',
    label: undefined,
    width: '100%',
    required: false,
    placeholder: '',
    pretty: false
  };

  render() {
    const {
      type,
      label,
      required,
      placeholder = '',
      width,
      options,
      className = '',
      id,
      pretty,
      onClear,
      value,
      ...attrs
    } = this.props;

    if (!options) return null;
    const isString = typeof label === 'string';

    const realValueOption = options.find(option => {
      return option.value === value
    });

    return (
      <div className="form-group">
        {isString ? (
          <label htmlFor={id}>
            <div className={required ? 'required-label' : ''}>{label}</div>
          </label>
        ) : null}
        {!pretty ? (
          <select type={type} className={className} required={required} id={id} {...attrs}>
            {placeholder ? (
              <option value="">{`${placeholder}${required ? '*' : ''}`}</option>
            ) : null}
            {options.map(option => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <span className="relative">
            <ReactSelect
              isOptionDisabled={(option) => option.disabled}
              options={options}
              required={required}
              placeholder={placeholder}
              simpleValue
              value={value === null || value === undefined ? null : realValueOption}
              isClearable={!!onClear}
              {...attrs}
            />
          </span>
        )}
      </div>
    );
  }
}
